import { refreshTokenApi } from "@/api/services/admin/auth";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { AdminRoutes, AppRoutes } from "@/routes/config";
import { clientApi, isAuth, setAxiosAuth } from "@/utils/api";
import { setLastAuthRoute } from "@/utils/lastAuthRoute";
import { redirectToLogin } from "@/utils/url";
import Cookies from "js-cookie";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const tryRefreshToken = async (): Promise<boolean> => {
  const refreshToken = Cookies.get("admin-refresh-token");
  const accessToken = Cookies.get("admin-token");
  if (!accessToken || !refreshToken) return false;

  try {
    await refreshTokenApi({ accessToken, refreshToken });
    return true;
  } catch {
    return false;
  }
};

export const withAuth: <CP extends {}>(WrappedComponent: FC<CP>) => FC<CP> = (WrappedComponent) => (props) => {
  const { pathname, search } = useLocation();
  const [isVerified, setIsVerified] = useState(false);
  const [userId] = useState(Cookies.get("admin-email"));
  const navigate = useNavigate();

  useEffect(() => {
    // The SSO hand-off page is a transient stop on the way to another product — remembering it would
    // bounce the next login into a redirect page instead of the last real screen the admin was on.
    if (isAuth() && pathname !== AppRoutes.mintOauthRedirect) {
      setLastAuthRoute(`${pathname}${search}`);
    }
  }, [pathname, search]);

  useEffect(() => {
    // Every 2 seconds check user id, if user id has changed then redirect to login page
    const intervalId = setInterval(() => {
      if (userId && userId != Cookies.get("admin-email")) {
        redirectToLogin();
      }
    }, 2000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      // set axios auth at init
      if (isAuth() && !clientApi.defaults.headers.common.Authorization) {
        setAxiosAuth();
      }

      // verify admin routes
      if (!isAuth() && AdminRoutes.some((path) => pathname.startsWith(path))) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          setAxiosAuth();
        } else {
          // redirectToLogin() clears the session itself; calling logout() first
          // would wipe the admin-email cookie before it can be carried to /login.
          redirectToLogin();
          return;
        }
      }

      setIsVerified(true);
    };

    verifyAuth();
  }, [pathname, navigate]);

  if (!isVerified) return <LoadingScreen />;

  return <WrappedComponent {...props} />;
};
